import React, { useMemo, useState } from 'react';
import * as Sheet from '../ui/sheet';
import { Skeleton } from '../ui/skeleton';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { AssignMentorProps, User } from '@/types';
import { FALLBACK_IMG } from '@/constants';
import { useGetAllClientUserListQuery } from '@/state';
import { useTenant } from '@/hooks';


const AssignMentor: React.FC<AssignMentorProps> = ({
  open,
  projectName,
  clientsLoading,
  onClose,
  onSelect,
  currentUserId,
}) => {
  const { tenant } = useTenant();

  const {
    data: clients = [],
    isLoading: userLoading,
    isFetching: userFetching,
  } = useGetAllClientUserListQuery(
    {
      tenant: tenant!,
    },
    {
      skip: !open,
    }
  );

  const [selectedMentorId, setSelectedMentorId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loading = clientsLoading || userLoading || userFetching;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedMentorId(null);
      setSearch('');
      onClose();
    }
  };

  // Sort mentors first, then by first name
  const sortedClients = useMemo(() => {
    return (clients as User[]).slice().sort((a, b) => {
      const aIsMentor = a.roles?.includes('mentor') ? 0 : 1;
      const bIsMentor = b.roles?.includes('mentor') ? 0 : 1;
      if (aIsMentor !== bIsMentor) return aIsMentor - bIsMentor;
      return a?.profile?.first_name?.localeCompare(b?.profile?.first_name);
    });
  }, [clients]);

  // Apply simple search on name or email
  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedClients;

    return sortedClients.filter((client) => {
      const fullName = `${client.profile.first_name} ${client.profile.last_name}`;
      return (
        fullName.toLowerCase().includes(term) ||
        client.email.toLowerCase().includes(term)
      );
    });
  }, [sortedClients, search]);

  const handleSelect = (id: string, name: string) => {
    setSelectedMentorId(id);
    onSelect(id, name);
  };

  const handleAssignMyself = () => {
    if (!currentUserId) return;
    setSelectedMentorId(currentUserId);
    onSelect(currentUserId, 'You');
  };

  return (
    <Sheet.Sheet open={open} onOpenChange={handleOpenChange}>
      <Sheet.SheetContent
        side="right"
        className="w-full pb-3 sm:w-[420px] md:w-[480px] overflow-y-hidden flex flex-col overflow-auto"
      >
        <Sheet.SheetHeader className="space-y-2">
          <Sheet.SheetTitle>Assign Mentor</Sheet.SheetTitle>
          <Sheet.SheetDescription>
            Select a mentor for{' '}
            <span className="font-semibold">{projectName}</span>
          </Sheet.SheetDescription>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <p className="text-xs text-muted-foreground">
              {loading
                ? 'Loading users...'
                : `Showing ${filteredClients.length} of ${clients.length} users${selectedMentorId ? ' • Mentor selected' : ''
                }`}
            </p>

            <Button
              size="sm"
              variant="outline"
              onClick={handleAssignMyself}
              disabled={!currentUserId}
            >
              Assign myself
            </Button>
          </div>

          {/* Single search input */}
          <Input
            placeholder="Search mentor by name or email"
            className="mt-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Sheet.SheetHeader>

        {/* List */}
        <ScrollArea className="mt-4 h-[calc(100vh-170px)] pr-3">
          <div className="space-y-2">
            {loading && (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
              </div>
            )}

            {!loading && filteredClients.length === 0 && (
              <Card className="p-3 ml-2 text-center text-sm text-muted-foreground">
                No users found.
              </Card>
            )}

            {!loading &&
              filteredClients.map((client) => {
                const isSelected = selectedMentorId === client.id;

                return (
                  <Card
                    key={client.id}
                    className={`p-3 ml-2 cursor-pointer transition border rounded-lg ${isSelected
                      ? 'border-primary bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                    onClick={() =>
                      handleSelect(
                        client.id,
                        `${client.profile.first_name} ` +
                        `${client.profile.last_name}`
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={client.profile.photo_url ?? FALLBACK_IMG}
                        className="h-10 w-10 rounded-full object-cover"
                        alt={client.profile.first_name}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium truncate">
                            {client.profile.first_name}{' '}
                            {client.profile.last_name}
                          </p>
                          {isSelected && (
                            <Badge
                              variant="outline"
                              className="text-[10px] uppercase tracking-wide"
                            >
                              Selected
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {client.email}
                        </p>
                        {client.roles?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {client.roles.map((role) => (
                              <Badge
                                key={role}
                                variant="outline"
                                className="text-[10px] uppercase tracking-wide"
                              >
                                {role}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
          </div>
        </ScrollArea>
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default AssignMentor;
